import React, { useState, useEffect, useRef } from "react";
import Navbar from "./Components/Navbar";
import Footer from "./Components/Footer";
import { useLanguage } from "../../contexts/LanguageContext";

// ─── Teks UI statis (bilingual) ────────────────────────────────────────────────
const UI_TEXT = {
  EN: {
    missionLabel: "Our Mission",
    missionTitle: "What We Stand For",
    missionBody:
      "The mission of Yayasan Rumoh Pangan Aceh is to build a sustainable, inclusive, and equitable food system through efforts to reduce food loss and food waste, improve community food security and self-sufficiency, empower local farmers and food business actors, develop regenerative and organic agriculture, and utilize surplus food to assist vulnerable groups. Furthermore, Rumoh Pangan Aceh is committed to strengthening collaboration between communities, government, the business sector, academics, and communities in creating a food ecosystem capable of improving community welfare while preserving the environment in Aceh.",

    valuesLabel: "Our Values",
    valuesTitle: "The Principles That Guide Us",

    values: [
      {
        key: "food-security",
        title: "Food Security",
        subtitle: "Ketahanan Pangan",
        body: "Committed to improving access, availability, and utilization of safe, nutritious, and sustainable food for all levels of society, especially vulnerable groups.",
      },
      {
        key: "sustainable-ag",
        title: "Sustainable Agriculture",
        subtitle: "Pertanian Berkelanjutan",
        body: "Encouraging the transformation of the agricultural system toward regenerative, organic, and environmentally friendly practices that improve farmers' welfare and preserve natural resources.",
      },
      {
        key: "solidarity",
        title: "Solidarity",
        subtitle: "Solidaritas",
        body: "Cultivating a spirit of care, mutual cooperation, and collaboration in addressing food issues through the involvement of communities, government, business, and various stakeholders.",
      },
      {
        key: "equity",
        title: "Equity",
        subtitle: "Keadilan",
        body: "Ensuring equal opportunities, access, and benefits for all people within the food system, and advocating for a fairer distribution of resources for farmers, food producers, and those in need.",
      },
      {
        key: "education",
        title: "Education",
        subtitle: "Edukasi",
        body: "Increasing public knowledge, awareness, and capacity regarding food security, food loss and waste reduction, nutrition, and sustainable agricultural practices through education and empowerment activities.",
      },
      {
        key: "food-bank",
        title: "Food Bank",
        subtitle: "Penyelamatan dan Berbagi Pangan",
        body: "Optimizing the use of still-edible surplus food to reduce food waste while helping to meet the food needs of those who require assistance.",
      },
    ],
  },

  ID: {
    missionLabel: "Misi Kami",
    missionTitle: "Apa yang Kami Perjuangkan",
    missionBody:
      "Misi Yayasan Rumoh Pangan Aceh adalah membangun sistem pangan yang berkelanjutan, inklusif, dan berkeadilan melalui upaya pengurangan susut dan sisa pangan (food loss and food waste), peningkatan ketahanan dan kemandirian pangan masyarakat, pemberdayaan petani dan pelaku usaha pangan lokal, pengembangan pertanian regeneratif dan organik, serta pemanfaatan pangan surplus untuk membantu kelompok rentan. Selain itu, Rumoh Pangan Aceh berkomitmen memperkuat kolaborasi antara masyarakat, pemerintah, dunia usaha, akademisi, dan komunitas dalam menciptakan ekosistem pangan yang mampu meningkatkan kesejahteraan masyarakat sekaligus menjaga kelestarian lingkungan di Aceh.",

    valuesLabel: "Nilai-Nilai Kami",
    valuesTitle: "Prinsip yang Memandu Kami",

    values: [
      {
        key: "food-security",
        title: "Food Security",
        subtitle: "Ketahanan Pangan",
        body: "Berkomitmen untuk meningkatkan akses, ketersediaan, dan pemanfaatan pangan yang aman, bergizi, dan berkelanjutan bagi seluruh lapisan masyarakat, khususnya kelompok rentan.",
      },
      {
        key: "sustainable-ag",
        title: "Sustainable Agriculture",
        subtitle: "Pertanian Berkelanjutan",
        body: "Mendorong transformasi sistem pertanian menuju praktik yang regeneratif, organik, ramah lingkungan, dan mampu meningkatkan kesejahteraan petani serta menjaga kelestarian sumber daya alam.",
      },
      {
        key: "solidarity",
        title: "Solidarity",
        subtitle: "Solidaritas",
        body: "Menumbuhkan semangat kepedulian, gotong royong, dan kolaborasi dalam mengatasi permasalahan pangan melalui keterlibatan masyarakat, komunitas, pemerintah, dunia usaha, dan berbagai pemangku kepentingan.",
      },
      {
        key: "equity",
        title: "Equity",
        subtitle: "Keadilan",
        body: "Memastikan kesempatan, akses, dan manfaat yang setara bagi seluruh masyarakat dalam sistem pangan, serta memperjuangkan distribusi sumber daya yang lebih adil bagi petani, produsen pangan, dan kelompok yang membutuhkan.",
      },
      {
        key: "education",
        title: "Education",
        subtitle: "Edukasi",
        body: "Meningkatkan pengetahuan, kesadaran, dan kapasitas masyarakat mengenai ketahanan pangan, pengurangan food loss dan food waste, gizi, serta praktik pertanian berkelanjutan melalui kegiatan pendidikan dan pemberdayaan.",
      },
      {
        key: "food-bank",
        title: "Food Bank",
        subtitle: "Penyelamatan dan Berbagi Pangan",
        body: "Mengoptimalkan pemanfaatan pangan surplus yang masih layak konsumsi untuk mengurangi pemborosan pangan sekaligus membantu memenuhi kebutuhan pangan masyarakat yang membutuhkan.",
      },
    ],
  },
};

// ─── Value icons (inline SVG paths) ───────────────────────────────────────────
const VALUE_ICONS = {
  "food-security": (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  "sustainable-ag": (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <path d="M12 2a10 10 0 000 20" />
      <path d="M12 2c2.5 5 4 10 4 10s-4 1-8 4" />
      <path d="M2 12h10" />
    </svg>
  ),
  solidarity: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  equity: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <line x1="12" y1="2" x2="12" y2="22" />
      <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
    </svg>
  ),
  education: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
      <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
    </svg>
  ),
  "food-bank": (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
    </svg>
  ),
};

// ─── useInView ─────────────────────────────────────────────────────────────────
const useInView = (ref, threshold = 0.1) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return inView;
};

// ─── Mission Section ───────────────────────────────────────────────────────────
const MissionSection = () => {
  const { language } = useLanguage();
  const tx = UI_TEXT[language];
  const ref = useRef(null);
  const inView = useInView(ref);

  return (
    <section ref={ref} className="bg-[#f7f3ec] py-24 px-4 md:px-8">
      <div className="container max-w-7xl mx-auto">
        <div
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(28px)",
            transition: "all 0.9s ease",
          }}
        >
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-[#c9862a]" />
            <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">
              {tx.missionLabel}
            </span>
          </div>

          <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1a1a18] leading-tight mb-8">
            {tx.missionTitle}
          </h2>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-8">
            <div className="flex-1 h-px bg-[#e8e0d0]" />
            <div className="w-2 h-2 bg-[#c9862a] rotate-45" />
            <div className="flex-1 h-px bg-[#e8e0d0]" />
          </div>

          <p className="text-[#4a4a44] text-[15px] leading-[1.9] text-justify max-w-4xl">
            {tx.missionBody}
          </p>
        </div>
      </div>
    </section>
  );
};

// ─── Values Section ────────────────────────────────────────────────────────────
const ValuesSection = () => {
  const { language } = useLanguage();
  const tx = UI_TEXT[language];
  const ref = useRef(null);
  const inView = useInView(ref, 0.05);

  return (
    <section ref={ref} className="bg-white py-6 px-4 md:px-6">
      <div className="max-w-[1400px] mx-auto">
        <div className="bg-[#1a4a35] px-8 md:px-16 lg:px-20 py-16 relative overflow-hidden">

          {/* Decorative watermark */}
          <div className="absolute bottom-0 right-0 font-serif text-[160px] font-bold text-white/[0.035] leading-none select-none pointer-events-none">
            VALUES
          </div>

          {/* Header */}
          <div
            className="mb-14"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "translateY(0)" : "translateY(24px)",
              transition: "all 0.8s ease",
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-10 bg-[#c9862a]" />
              <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">
                {tx.valuesLabel}
              </span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-white leading-tight">
              {tx.valuesTitle}
            </h2>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-14">
            <div className="flex-1 h-px bg-white/10" />
            <div className="w-2 h-2 bg-[#c9862a] rotate-45" />
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Values grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {tx.values.map((v, i) => (
              <div
                key={v.key}
                className="bg-[#1a4a35] p-8 group"
                style={{
                  opacity: inView ? 1 : 0,
                  transform: inView ? "translateY(0)" : "translateY(24px)",
                  transition: `all 0.7s ease ${0.1 + i * 0.08}s`,
                }}
              >
                {/* Icon */}
                <div className="text-[#c9862a] mb-5 w-10 h-10 flex items-center justify-center border border-[#c9862a]/30 group-hover:bg-[#c9862a]/10 transition-colors">
                  {VALUE_ICONS[v.key]}
                </div>

                {/* Title */}
                <h3 className="font-serif text-lg font-bold text-white mb-1 leading-snug">
                  {v.title}
                </h3>
                <p className="text-[#c9862a] text-xs font-semibold tracking-[2px] uppercase mb-4">
                  {v.subtitle}
                </p>

                {/* Thin divider */}
                <div className="w-8 h-px bg-white/20 mb-4" />

                {/* Body */}
                <p className="text-white/65 text-sm leading-[1.85] text-justify">
                  {v.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────
const OurMissionValues = () => (
  <main className="overflow-x-hidden font-homepage bg-white">
    <Navbar />
    <section id="mission">
      <MissionSection />
    </section>
    <section id="values">
      <ValuesSection />
    </section>
    <Footer />
  </main>
);

export default OurMissionValues;